import { Link } from "react-router-dom";
import { Network, ShieldCheck, TrendingDown, Package, FileText, Building2, Landmark } from "lucide-react";
import { COMPANY, IMAGES, BROCHURE_URL } from "@/lib/company";
import { openAndDownloadDossier } from "@/lib/download";

const model = [
  { icon: Network, title: "We Curate, Not Just Sell", text: "For every order, we select the optimal batch based on real-time CoA data, price, and availability." },
  { icon: ShieldCheck, title: "You Are Never Hostage to One Factory", text: "If one facility faces capacity issues, we pivot seamlessly to another qualified source." },
  { icon: TrendingDown, title: "Competitive Pricing", text: "We aggregate volumes across our network and negotiate terms no single small buyer can achieve alone." },
  { icon: Package, title: "Small MOQ Friendly", text: "Start with 25 kg trial orders. We consolidate production so you don't commit to factory-level minimums." },
];

const challenges = [
  { c: "You don't want to manage 5 Indian suppliers", s: "You manage one: us. We handle the factory network, quality verification, and documentation." },
  { c: "You need TGA / USP / BP compliant documentation", s: "We compile batch-specific CoAs, GMP certs, MSDS, and regulatory letters for your quality team." },
  { c: "You fear quality inconsistency", s: "We conduct pre-shipment verification against your specification. If it doesn't match, it doesn't ship." },
  { c: "You need 25 kg, not 500 kg", s: "We aggregate and consolidate production. Start small, scale confidently." },
  { c: "You don't understand Indian export logistics", s: "We do. FOB, CIF, or DDP — we handle freight, customs, and delivery." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.ashwagandha} alt="Botanical roots" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-24 w-full">
          <span className="inline-block px-4 py-1.5 bg-brand-gold text-brand-green-dark text-[11px] font-bold uppercase tracking-[0.25em] rounded-sm mb-6">About Us</span>
          <h1 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            From India's Finest Labs<br />
            <span className="text-brand-gold">to the World</span> — Curated, Verified, Delivered.
          </h1>
          <p className="mt-6 text-white/85 text-lg max-w-2xl">
            Aad Labs is not a manufacturer. We are something more valuable to global buyers — your professional sourcing partner, quality gatekeeper, and export logistics arm in India.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="about-cta-quote" className="btn-gold">Request a Quote</Link>
            <a href={BROCHURE_URL} download="Aad-Labs-Export-Dossier.pdf" target="_blank" rel="noopener noreferrer" onClick={openAndDownloadDossier} data-testid="about-cta-brochure" className="btn-outline-white">Download Company Profile</a>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Who We Are</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline">
              A Faridabad-based export trading house<br />
              <span className="text-brand-green">connecting India to the world.</span>
            </h2>
            <div className="mt-8 space-y-5 text-brand-charcoal/85 leading-relaxed">
              <p>
                {COMPANY.name} is a Faridabad, Haryana-based export trading company specializing in premium botanical extracts, spices, and agricultural commodities. Founded and led by Director <strong>{COMPANY.director}</strong>, we leverage our deep network of WHO-GMP and ISO-certified manufacturers across India to deliver pharmaceutical-grade ingredients to global markets.
              </p>
              <p>
                Unlike single-factory agents, we maintain qualified relationships with multiple manufacturers. This allows us to select the optimal batch for every order based on real-time CoA data, ensure supply continuity, offer competitive pricing through volume aggregation, and provide smaller MOQs by consolidating production across facilities.
              </p>
            </div>
          </div>
          <div>
            <img src={IMAGES.warehouse} alt="Export warehouse" className="rounded-lg shadow-xl w-full h-[500px] object-cover" />
          </div>
        </div>
      </section>

      {/* Our Model */}
      <section className="py-24 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Our Model</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl gold-underline-center">
              The Professional Trader
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {model.map((m, i) => (
              <div key={i} data-testid={`model-card-${i}`} className="bg-white p-8 rounded-lg border border-neutral-200 hover:border-brand-gold transition-colors">
                <div className="w-12 h-12 rounded-md bg-brand-green flex items-center justify-center mb-5">
                  <m.icon className="w-6 h-6 text-brand-gold" />
                </div>
                <h3 className="font-display font-bold text-brand-charcoal text-lg">{m.title}</h3>
                <p className="mt-3 text-sm text-brand-charcoal/75 leading-relaxed">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Heritage in Trade */}
      <section className="py-20 bg-white border-y-2 border-brand-gold">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Our Heritage</span>
          <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl">Our Heritage in International Trade</h2>
          <p className="mt-6 text-brand-charcoal/85 leading-relaxed text-lg">
            While Aad Labs was formally established in 2026, our roots in international trade run deeper. With over <strong>two years of hands-on experience</strong> in import-export operations — including customs clearance, freight forwarding, and regulatory documentation — we understand the friction points of cross-border trade intimately. We have handled the paperwork, navigated the port authorities, and managed the logistics. Now, we bring that operational excellence to the botanical extracts and agricultural commodities sector.
          </p>
        </div>
      </section>

      {/* Regulatory Footprint */}
      <section className="py-24 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Regulatory Footprint</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline-center">
              Fully Compliant. Fully Documented.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: FileText, title: "IEC Registered", body: `Import Export Code ${COMPANY.iec} — Directorate General of Foreign Trade, Ministry of Commerce, Government of India.` },
              { icon: ShieldCheck, title: "FSSAI Central License", body: `License No. ${COMPANY.fssai} — Food Safety and Standards Authority of India, Central Licensing.` },
              { icon: Landmark, title: "GST Compliant", body: `GSTIN ${COMPANY.gstin} — Haryana State Registration.` },
            ].map((c, i) => (
              <div key={i} data-testid={`regulatory-card-${i}`} className="bg-white p-8 rounded-lg border border-neutral-200 hover:border-brand-green transition-colors">
                <div className="w-14 h-14 rounded-md bg-brand-gold/15 flex items-center justify-center mb-5">
                  <c.icon className="w-7 h-7 text-brand-gold" />
                </div>
                <h3 className="font-display font-bold text-brand-charcoal text-xl">{c.title}</h3>
                <p className="mt-3 text-sm text-brand-charcoal/75 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Global Buyers Choose */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Why Global Buyers Choose Us</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline-center">
              Your Challenge → Our Solution
            </h2>
          </div>
          <div className="space-y-4">
            {challenges.map((row, i) => (
              <div key={i} data-testid={`challenge-row-${i}`} className="grid md:grid-cols-5 gap-4 p-6 bg-brand-cream rounded-lg border-l-4 border-brand-gold hover:border-brand-green transition-colors">
                <div className="md:col-span-2">
                  <div className="text-xs uppercase tracking-widest text-brand-gold font-bold mb-1">Your Challenge</div>
                  <div className="font-semibold text-brand-charcoal">{row.c}</div>
                </div>
                <div className="md:col-span-3">
                  <div className="text-xs uppercase tracking-widest text-brand-green font-bold mb-1">Our Solution</div>
                  <div className="text-brand-charcoal/85">{row.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Quote */}
      <section className="py-24 bg-brand-green text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-brand-gold font-accent text-8xl leading-none">"</div>
          <blockquote className="font-accent italic text-2xl md:text-4xl leading-snug -mt-4">
            We don't just move products across borders. We move trust.
          </blockquote>
          <div className="mt-6 text-brand-gold text-sm uppercase tracking-widest font-bold">— {COMPANY.director}, Director</div>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" data-testid="commitment-cta-quote" className="btn-gold">Request a Quote</Link>
            <a href={`mailto:${COMPANY.email}?subject=Video Call Request`} data-testid="commitment-cta-video" className="btn-outline-white">Schedule a Video Call</a>
          </div>
        </div>
      </section>

      {/* Contact strip */}
      <section className="py-16 bg-[#212121] text-white">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display font-bold text-3xl">Ready to Explore a Partnership?</h3>
            <p className="mt-3 text-white/70">Whether you need a 25 kg sample or a 500 kg recurring order, Aad Labs is your reliable Indian sourcing partner.</p>
          </div>
          <div className="text-sm space-y-2 md:text-right">
            <div>Email: <a href={`mailto:${COMPANY.email}`} className="text-brand-gold hover:underline">{COMPANY.email}</a></div>
            <div>Phone: <a href={`tel:${COMPANY.phoneRaw}`} className="text-brand-gold hover:underline">{COMPANY.phone}</a></div>
            <Link to="/contact" data-testid="about-footer-cta" className="btn-gold mt-3 inline-flex">Contact Us Today</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
